/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {dlsLayout} from '../../theme/theme';
import {FilterDrawerCategoryBase} from './types';

/**
 * Gets the configured filter drawer width from the active DLS theme.
 *
 * ```typescript
 * import {getFilterDrawerWidth} from '@ion/dls/src/components/drawer/utils';
 *
 * ...
 *
 * const drawerWidth = getFilterDrawerWidth();
 * ```
 *
 * @returns `624` when no theme width is configured, otherwise the theme value.
 */
export const getFilterDrawerWidth = () => dlsLayout('filterDrawer', 'width') || 624;

/**
 * Orders filter drawer categories from a persisted category id sequence.
 *
 * @param categories    Current filter drawer categories.
 * @param categoryOrder Persisted category id order.
 * @returns Ordered categories with unknown ids ignored and new categories appended.
 */
export const orderFilterDrawerCategories = (
  categories: FilterDrawerCategoryBase[],
  categoryOrder: string[] = []
) => {
  const categoryLookup = categories?.reduce((lookup, category) => ({
    ...lookup,
    [category.id] : category
  }), {} as Record<string, FilterDrawerCategoryBase>);
  const orderedCategories = categoryOrder
    .map(id => categoryLookup[id])
    .filter((category): category is FilterDrawerCategoryBase => !!category);
  const orderedIds = new Set(orderedCategories.map(({id}) => id));
  return [
    ...orderedCategories,
    ...categories?.filter(({id}) => !orderedIds.has(id))
  ];
};

/**
 * Merges incoming categories into the user's current drawer category order.
 *
 * ```typescript
 * import {mergeFilterDrawerCategories} from '@ion/dls/src/components/drawer/utils';
 *
 * ...
 *
 * const mergedCategories = mergeFilterDrawerCategories(currentCategories, categories);
 * ```
 *
 * @param  currentCategories  Current drawer category order.
 * @param  nextCategories     Latest category definitions.
 * @returns                   Ordered categories with removed items discarded.
 */
export const mergeFilterDrawerCategories = (
  currentCategories: FilterDrawerCategoryBase[],
  nextCategories: FilterDrawerCategoryBase[]
) => {
  const nextCategoryLookup = nextCategories.reduce((lookup, category) => ({
    ...lookup,
    [category.id] : category
  }), {} as Record<string, FilterDrawerCategoryBase>);
  const currentIds = currentCategories.map(({id}) => id);
  const orderedCategories = currentCategories
    .filter(({id}) => nextCategoryLookup[id])
    .map(category => ({
      ...category,
      ...nextCategoryLookup[category.id]
    }));
  const addedCategories = nextCategories.filter(({id}) => !currentIds.includes(id));
  return currentCategories.length ? [...orderedCategories, ...addedCategories] : nextCategories;
};
